import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useInstalledEquipmentQuery } from '@/api/appSolutionBuilder/filterUnit';
import { ProductItemResponse } from '@/api/product/types';
import { AutoComplete, InputText } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import {
	FilterInsertRegistrationRequest,
	FilterInsertRegistrationUpdateRequest,
	FilterUnitRegistration,
	FilterUnitRegistrationRequest,
	FilterUnitRegistrationUpdateRequest,
} from '@/generated/data.schemas';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { useDebounce } from '@/helpers';
import { SelectItem } from '@/types';
import styles from '../FilterUnit.module.scss';

interface FilterUnitFormProps {
	formId: 'register-filerUnit-form' | 'edit-filerUnit-form';
	legend?: string;
	filterUnit?: FilterUnitRegistration;
	accountId: number;
	validationErrors: Record<string, string[]> | null;
	productItems?: ProductItemResponse[] | null;
	handleSubmit: (e) => void;
	handleChange: () => void;
}

export const FilterUnitForm: React.FunctionComponent<FilterUnitFormProps> = ({
	formId,
	legend,
	filterUnit,
	validationErrors,
	handleSubmit,
	handleChange,
}) => {
	const initialMount = React.useRef(true);
	const { segmentationId } = useContext(SegmentationContext);
	const { data: settings } = useGetSettings(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);

	const [articleNumber, setArticleNumber] = useState<string>(filterUnit?.articleNumber ?? '');
	const [insertProductId, setInsertProductId] = useState(filterUnit?.inserts?.[0].articleNumber ?? '');
	const [filterName, setFilterName] = useState<string>(filterUnit?.name ?? '');

	const debouncedFilterId = useDebounce(articleNumber, 500);

	const formatDate = (filterUnitDate: string | null | undefined) => {
		if (typeof filterUnitDate !== 'string') return undefined;

		const date = new Date(filterUnitDate);

		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
		const day = String(date.getDate()).padStart(2, '0');

		// Return formatted date in YYYY-MM-DD to match input type date
		return `${year}-${month}-${day}`;
	};

	const { data: installedEquipments, isLoading } = useInstalledEquipmentQuery(
		debouncedFilterId,
		settings?.data.searchSettings,
		!initialMount.current,
	);

	const [forceClose, setForceClose] = useState<boolean>(false);

	const installedEquipmentsOptions: SelectItem[] = useMemo(() => {
		if (!isLoading && installedEquipments) {
			if (installedEquipments.length > 0) {
				return installedEquipments.map((x) => {
					// x.id comes as the second element,
					// because there are cases that have similar skuNo (CCJ item No assigned to the master product),
					// but the id is always unique, so it can be used for calculations.
					// The first element is only for showing as autocomplete selected value and the label is what is shown in the dropdown.
					return {
						value: [x.skuNo ?? x.id, x.id],
						label: `${x.name} (${x.skuNo ?? x.id})`,
					};
				});
			}
			return [
				{
					value: [articleNumber],
					label: translations?.data.filterRegistrations.noItemFound || 'Not found',
				},
			];
		}
		return [];
	}, [installedEquipments, articleNumber, translations, isLoading]);

	const modifyFilterId = (e) => {
		setArticleNumber(e.target.value);
		initialMount.current = false;
	};

	const resetFilter = () => {
		// Filter is not found but the user wants to use it anyway
		setFilterName('');
		setInsertProductId('');
	};

	const selectFilter = (option: SelectItem) => {
		const selected = installedEquipments?.find((x) => x.id === option.value?.[1]);
		if (selected) {
			setInsertProductId(selected.insertProductId ?? '');
			setFilterName(selected.name ?? '');
		} else {
			resetFilter();
		}
		handleChange();
	};

	const modifyFilterName = (e) => {
		setFilterName(e.target.value);
		handleChange();
	};

	const modifyInsertArticleNumber = (e) => {
		setInsertProductId(e.target.value);
		handleChange();
	};

	const submitForm = (e) => {
		e.preventDefault();
		const formData = new FormData(e.target);

		const numberOfInserts = formData.get('insertQuantity') ?? filterUnit?.numberOfInserts ?? 1;
		const saleDate = formData.get('saleDate');
		const installationDate = formData.get('installationDate');
		const insertLastChangedDate = formData.get('insertLastChangedDate');

		const filterUnitInsertForm: FilterInsertRegistrationRequest = {
			articleNumber: String(formData.get('insertArticleNumber') ?? ''),
			name: String(formData.get('insertName') ?? ''),
			batchNumber: String(formData.get('batchNumber') ?? ''),
			saleDate: saleDate ? String(saleDate) : null,
			installationDate: installationDate ? String(installationDate) : null,
			lastChangedDate: insertLastChangedDate ? String(insertLastChangedDate) : null,
		};

		const payload: FilterUnitRegistrationRequest | FilterUnitRegistrationUpdateRequest = {
			articleNumber: String(formData.get('articleNumber')),
			insert:
				formId === 'register-filerUnit-form'
					? (filterUnitInsertForm as FilterInsertRegistrationRequest)
					: ({
							...filterUnitInsertForm,
							id: filterUnit?.inserts?.[0].id,
						} as FilterInsertRegistrationUpdateRequest),
			insertType: formData.get('insertType')?.toString() ?? '',
			name: String(formData.get('filterName')),
			numberOfInserts: numberOfInserts ? Number(numberOfInserts) : null,
			orderNo: formData.get('orderNo')?.toString() ?? '',
		};
		handleSubmit(payload);
	};

	useEffect(() => {
		if (
			!initialMount.current &&
			installedEquipments &&
			articleNumber === filterUnit?.articleNumber &&
			formId === 'edit-filerUnit-form'
		) {
			const payload: SelectItem = {
				// calculations in the selectFilter function expect the second element to be the id. check the comment  in the installedEquipmentsOptions
				value: [null, filterUnit?.articleNumber],
				label: filterUnit.name ?? '',
			};
			selectFilter(payload);
			setForceClose(true);
			window.setTimeout(() => {
				setForceClose(false);
			}, 5);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [installedEquipments]);

	return (
		<form id={formId} onSubmit={submitForm}>
			<fieldset className={styles.fieldset}>
				<legend className="u-hide-visually">{legend}</legend>
				<InputText
					id={'filterName'}
					name={'filterName'}
					value={filterName}
					label={translations?.data.filterRegistrations.name}
					variant={'inverse'}
					onChange={modifyFilterName}
				/>
				<AutoComplete
					options={installedEquipmentsOptions}
					id={'articleNumber'}
					label={translations?.data.filterRegistrations.articleNo || 'Article No.'}
					onSelect={selectFilter}
					onChange={modifyFilterId}
					name={'articleNumber'}
					value={articleNumber}
					variant={'inverse'}
					forceClose={forceClose}
				/>

				<InputText
					name="batchNumber"
					id="batchNumber"
					defaultValue={filterUnit?.inserts?.[0].batchNumber ?? ''}
					label={translations?.data.filterRegistrations.batchNo}
					errorText={validationErrors?.BatchNumber?.[0]}
					onChange={handleChange}
					variant={'inverse'}
				/>
				<InputText
					name="orderNo"
					id="orderNo"
					defaultValue={filterUnit?.orderNo ?? ''}
					label={translations?.data.filterRegistrations.orderNo}
					errorText={validationErrors?.BatchNumber?.[0]}
					onChange={handleChange}
					variant={'inverse'}
				/>
				<InputText
					type="date"
					name="saleDate"
					id="saleDate"
					defaultValue={formatDate(filterUnit?.inserts?.[0].saleDate) ?? ''}
					label={translations?.data.filterRegistrations.dateOfSale}
					onChange={handleChange}
					variant={'inverse'}
				/>
				<InputText
					type="date"
					name="installationDate"
					id="installationDate"
					defaultValue={formatDate(filterUnit?.inserts?.[0].installationDate) ?? ''}
					label={translations?.data.filterRegistrations.installationDate}
					onChange={handleChange}
					variant={'inverse'}
				/>

				<p className={styles.insertHeadline}>{translations?.data.filterRegistrations.filterInsertHeadline}</p>

				<InputText
					defaultValue={filterUnit?.inserts?.[0].name ?? ''}
					label={translations?.data.filterRegistrations.insertName}
					variant={'inverse'}
					name={'insertName'}
				/>

				<InputText
					name="insertArticleNumber"
					id="insertArticleNumber"
					value={insertProductId}
					label={translations?.data.filterRegistrations.articleNo}
					errorText={validationErrors?.['Insert.ArticleNumber']?.[0]}
					onChange={modifyInsertArticleNumber}
					variant={'inverse'}
				/>

				<InputText
					type="date"
					name="insertLastChangedDate"
					id="insertLastChangedDate"
					defaultValue={formatDate(filterUnit?.inserts?.[0].lastChangedDate) || ''}
					label={translations?.data.filterRegistrations.lastChangedDate}
					onChange={handleChange}
					variant={'inverse'}
				/>
			</fieldset>
		</form>
	);
};
